import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import PlanDemo from '../Planning/PlanDemo';
import './NoPlanCard.scss';

type Props = {
    address?: string;
    revenue: number;
};

const NoPlanCard = ({address, revenue}: Props) => {
    const viewportRef = useRef<HTMLDivElement>(null);
    const [activeSlide, setActiveSlide] = useState(0);

    useEffect(() => {
        const alignSlide = () => {
            const viewport = viewportRef.current;
            if (viewport) viewport.scrollLeft = activeSlide * viewport.clientWidth;
        };
        window.addEventListener('resize', alignSlide);
        return () => window.removeEventListener('resize', alignSlide);
    }, [activeSlide]);

    const goTo = (index: number) => {
        const viewport = viewportRef.current;
        if (!viewport) return;
        const left = index * viewport.clientWidth;
        const reducedMotion = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (typeof viewport.scrollTo === 'function') {
            try {
                viewport.scrollTo({left, behavior: reducedMotion ? 'auto' : 'smooth'});
                return;
            } catch {
                // Older webviews support scrollLeft but not scrollTo options.
            }
        }
        viewport.scrollLeft = left;
    };

    const onScroll = () => {
        const viewport = viewportRef.current;
        if (!viewport || !viewport.clientWidth) return;
        const index = Math.max(0, Math.min(1, Math.round(viewport.scrollLeft / viewport.clientWidth)));
        setActiveSlide(current => current === index ? current : index);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowRight' && activeSlide === 0) {
            event.preventDefault();
            goTo(1);
        } else if (event.key === 'ArrowLeft' && activeSlide === 1) {
            event.preventDefault();
            goTo(0);
        }
    };

    const safeRevenue = Number.isFinite(revenue) ? revenue : 0;

    return (
        <div className={'home-planning-card no-plan home-no-plan-carousel ' + (activeSlide === 1 ? 'is-example-active' : '')}>
            <div
                className='home-no-plan-viewport'
                ref={viewportRef}
                onScroll={onScroll}
                onKeyDown={onKeyDown}
                tabIndex={0}
                role='region'
                aria-label='План на сегодня. Свайпните влево, чтобы увидеть пример.'
            >
                <div className='home-no-plan-slide' role='group' aria-label='Ваши показатели'>
                    <div className='home-planning-header'>
                        <div className='home-planning-heading'>
                            <Link to='/planning' className='home-no-plan-title-link'>
                                <span className='home-planning-title'>План на сегодня</span>
                                <span aria-hidden='true'>›</span>
                            </Link>
                            {address ? <div className='home-planning-address'>{address}</div> : null}
                        </div>
                        <div className='home-planning-percent'>
                            <small className='home-planning-empty-status'>План пока не задан</small>
                        </div>
                    </div>
                    <div className='home-planning-revenue-label'>Моя выручка за сегодня</div>
                    <div className='home-planning-revenue'>{safeRevenue.toLocaleString('ru-RU')} ₽</div>
                    <p className='home-planning-empty-note'>План подскажет, сколько ещё стрижек или окрашиваний нужно до цели.</p>
                    <div className='home-no-plan-swipe-prompt'>
                        <span>Пример плана — свайпните влево</span>
                        <span aria-hidden='true'>←</span>
                    </div>
                </div>
                <div className='home-no-plan-slide home-no-plan-slide-demo' role='group' aria-label='Пример плана'>
                    <PlanDemo/>
                </div>
            </div>
            <div className='home-no-plan-dots' role='group' aria-label='Показать страницу карточки'>
                {['Ваши данные', 'Пример плана'].map((label, index) => (
                    <button
                        key={label}
                        type='button'
                        className={'home-no-plan-dot ' + (activeSlide === index ? 'is-active' : '')}
                        aria-label={label}
                        aria-current={activeSlide === index ? 'step' : undefined}
                        onClick={() => goTo(index)}
                    ><span/></button>
                ))}
            </div>
        </div>
    );
};

export default NoPlanCard;
